/**
 * The logo marquee, ported from the live homepage and shared by every page
 * that shows it.
 *
 * It lived inside Pricing.tsx until /partner needed the same band. Two copies
 * of a logo wall drift within a week: one page gains a partner, the other
 * keeps eleven, and nobody notices until a partner asks why they are on one
 * page and not the other.
 *
 * The mechanics (48px gap, 30s linear loop, greyscale to colour on hover,
 * 60px fade edges, hover to pause) are read off `.lc-*` on www.pulseofp3.org
 * so the two surfaces run the same band. Styling lives in gateway.css under
 * `.eg-lc`; only the data and the markup are here.
 */

interface Partner {
  readonly src: string;
  readonly name: string;
  /**
   * OPTICAL HEIGHT, in px. Twelve marks drawn by twelve studios do not share
   * a bounding box: MENTOR is a tall stacked lockup where most of the height
   * is a glyph above the word, Gilead is a wide one, and Leo carries a line
   * of small type unreadable at the height that suits the others. One shared
   * max-height renders that as a wall of mismatched weights, so each mark is
   * sized to LOOK like the same size, which is the only rule a logo wall has
   * ever obeyed.
   */
  readonly cap: number;
}

/* THE HOMEPAGE'S OWN TWELVE, IN ITS OWN ORDER, AT ITS OWN HEIGHTS.
   Read off the live `.lc-track` on www.pulseofp3.org rather than reassembled:
   each logo carries the exact inline height the homepage gives it (32 or 36),
   which is that page's optical balancing and is not worth re-deriving. Files
   are self-hosted copies of the same artwork the homepage serves. */
const PARTNERS: readonly Partner[] = [
  { src: "/img/logos/csu.png", name: "Chicago State University", cap: 32 },
  { src: "/img/logos/xavier.png", name: "Xavier University", cap: 32 },
  { src: "/img/logos/langston.png", name: "Langston University", cap: 36 },
  { src: "/img/logos/nyu.png", name: "New York University", cap: 32 },
  { src: "/img/logos/google.png", name: "Google", cap: 32 },
  { src: "/img/logos/lsu.png", name: "Louisiana State University", cap: 32 },
  { src: "/img/logos/mbk.png", name: "My Brother's Keeper", cap: 36 },
  { src: "/img/logos/project-hood.png", name: "Project H.O.O.D.", cap: 36 },
  { src: "/img/logos/cps.png", name: "Chicago Public Schools", cap: 36 },
  { src: "/img/logos/100bm.png", name: "100 Black Men", cap: 36 },
  { src: "/img/logos/gilead.png", name: "Gilead Sciences", cap: 36 },
  { src: "/img/logos/lurie.png", name: "Lurie Children's Hospital", cap: 36 },
];

export function LogoCarousel({ headingId = "partners-heading" }: { headingId?: string }) {
  return (
    <section className="eg-lc" aria-labelledby={headingId}>
      <h2 className="eg-lc-label" id={headingId}>
        Trusted by leading institutions
      </h2>
      <div className="eg-lc-wrap">
        <div className="eg-lc-fade is-l" aria-hidden="true" />
        <div className="eg-lc-fade is-r" aria-hidden="true" />
        {/* The track holds the list TWICE and translates by -50%, so the
            second copy is exactly where the first started when the animation
            loops. That is why the seam is invisible, and it is why the
            duplicate is aria-hidden rather than removed. */}
        <div className="eg-lc-track">
          {PARTNERS.map((partner) => (
            <img
              key={partner.name}
              src={partner.src}
              alt={partner.name}
              style={{ height: `${partner.cap}px` }}
              loading="lazy"
              decoding="async"
            />
          ))}
          {PARTNERS.map((partner) => (
            <img
              key={`${partner.name}-loop`}
              src={partner.src}
              alt=""
              aria-hidden="true"
              style={{ height: `${partner.cap}px` }}
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
